/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Button, DefinitionTooltip, Dropdown } from '@carbon/react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { processApplicationStore } from 'stores';
import { defineStagesModalStore } from 'App/Pages/Diagram/Deployment/defineStagesModal';

import useSelectFirstAssignedStageOnMount from './use-select-first-assigned-stage';
import { STAGES } from './stages';
import useSelectClusterOnStageChange from './use-select-cluster-on-stage-change';

function StageSelection({ mode, clusters, setSelectedCluster, selectedStage, setSelectedStage, closeDeployModal }) {
  const { defaultDevClusterId, defaultTestClusterId, defaultStageClusterId, defaultProdClusterId } =
    processApplicationStore.processApplication;

  const stages = [
    {
      key: STAGES.DEVELOPMENT,
      label: 'Development',
      clusterId: defaultDevClusterId,
      disabled: true,
      cluster: 'Unassigned'
    },
    { key: STAGES.TESTING, label: 'Testing', clusterId: defaultTestClusterId, disabled: true, cluster: 'Unassigned' },
    { key: STAGES.STAGING, label: 'Staging', clusterId: defaultStageClusterId, disabled: true, cluster: 'Unassigned' },
    {
      key: STAGES.PRODUCTION,
      label: 'Production',
      clusterId: defaultProdClusterId,
      disabled: true,
      cluster: 'Unassigned'
    }
  ];
  stages.forEach((stage) => {
    const isClusterAssigned = stage.clusterId;
    if (isClusterAssigned) {
      const cluster = clusters?.find((cluster) => cluster.id === stage.clusterId);

      // If assigned cluster still present in the configuration
      if (cluster) {
        stage.cluster = `${cluster.name} (${cluster.version})`;
        stage.disabled = false;
      }
    }
  });

  useSelectFirstAssignedStageOnMount(stages, setSelectedStage);

  useSelectClusterOnStageChange(
    selectedStage,
    clusters,
    setSelectedCluster,
    defaultDevClusterId,
    defaultTestClusterId,
    defaultStageClusterId,
    defaultProdClusterId
  );

  return (
    clusters.length > 0 && (
      <Container>
        <_Dropdown
          id="cluster-selection-dropdown"
          titleText={
            <DefinitionTooltip
              definition={
                <span role="tooltip">
                  Select the PDLC stage for deployment. Use the "Define stages" button to assign clusters to stages.
                </span>
              }
              openOnHover
              align="right"
            >
              Stage
            </DefinitionTooltip>
          }
          onChange={({ selectedItem }) => {
            // @ts-ignore
            setSelectedStage(selectedItem.key);
          }}
          selectedItem={stages.find((stage) => stage.key === selectedStage) || null}
          label="Select a stage"
          aria-label="Select a stage"
          items={stages}
          // @ts-ignore
          itemToString={(stage) => stage.label}
          itemToElement={({ label, cluster }) => (
            <Stage>
              <StageLabel>{label}</StageLabel>
              <ClusterAssignment>{cluster}</ClusterAssignment>
            </Stage>
          )}
        />
        <Button
          size="md"
          kind="tertiary"
          data-test="define-stages-button"
          onClick={() => {
            closeDeployModal();
            defineStagesModalStore.open({ executeModalMode: mode });
          }}
        >
          Define stages
        </Button>
      </Container>
    )
  );
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 10px;
`;

const _Dropdown = styled(Dropdown)`
  width: 400px;
  flex: 1;

  .cds--popover-content {
    min-width: 480px;
  }
`;

const Stage = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StageLabel = styled.span`
  width: 150px;
  text-align: left;
`;

const ClusterAssignment = styled.span`
  flex-grow: 1;
  text-align: left;
`;

export default observer(StageSelection);
